'use client';

/**
 * AddToCartMobile Component
 *
 * This component renders a numeric input field for use in mobile views, but may also be used for desktop.
 *
 * Props:
 * - code
 * - title: The name of the product
 * - showToast: The type of toast to display
 * - disabled: Boolean to disable the add to cart button
 * - label: Custom label for the Add To Cart button if needed
 *
 * State:
 * - quantity: Manages the state of the numeric value in the field
 * - loading: Manages the loading state
 *
 * Usage:
 * The component uses `AddToCartMobile` to allow users to add the product to the cart and displays
 * an alert message when a product is successfully added.
 */


// Package Imports
import React, { useState } from 'react';
import NumericInput from 'react-numeric-input';
import classNames from 'classnames';

// Function Imports
import { AddToCart } from '@/lib/functions/addToCart';
import { useOrder } from '@/lib/context/orderContext';

// Component Imports
import StylizedButton from '@/components/stylizedButton';

// Style Imports
import styles from './addToCartMobile.module.scss';

interface AddToCartMobileProps {
  code: string;
  title: string;
  caseQuantity?: number;
  showToast: (type: 'error' | 'success' | 'notice', message: string) => void;
  disabled?: boolean;
  label?: string;
  isFeatured?: boolean;
  className?: string;
}

const AddToCartMobile: React.FC<AddToCartMobileProps> = ({
  code,
  title,
  caseQuantity,
  showToast,
  disabled = false,
  label,
  isFeatured = false,
  className = '',
}) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const { state: orderState, dispatch: orderDispatch } = useOrder();

  const handleAddToCart = async () => {
    if (!code || quantity < 1 || disabled) return;

    // Determine if single product or case
    let finalQuantity = quantity;
    if (caseQuantity) {
      finalQuantity = quantity * caseQuantity;
    }

    try {
      setLoading(true);
      const res = await AddToCart(code, finalQuantity, orderState, orderDispatch);
      if (res.success) {
        showToast('success', `${quantity} × "${title}" has been added to your cart.`);
      } else {
        // TODO: Need to add No Stock at Store error message
        showToast('error', `Failed to add ${quantity} × "${title}" to your cart.`);
      }
    } catch (err) {
      console.error(err);
      showToast('error', `An error occurred while adding to cart.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classNames(
      styles.wrapper, 
      {
        [styles[className]]: className,
        [styles.featuredQuantity]: isFeatured,
      })
    }>
      <NumericInput
        mobile
        min={1}
        value={quantity}
        className={styles.input}
        onChange={(value: number | null) => {
          if (value && value > 0) setQuantity(value);
        }}
      />
      <StylizedButton
        className={styles.button}
        size="mini"
        loading={loading}
        disabled={disabled || loading}
        handleClick={handleAddToCart}
      >
        {label ?? 'Add to Cart'}
      </StylizedButton>
    </div>
  );
};

export default AddToCartMobile;
